/**
 * useHitlSubmitLatch — keeps a HITL question's Approve/Reject controls disabled
 * from the moment the user submits until the substrate advances to a DIFFERENT
 * gate, surviving status-poll churn AND remounts of the question panel.
 *
 * Why a hook (and why the CALLER must host it in a stable parent):
 *
 *   The /resume POST returns within milliseconds — well before the substrate's
 *   warm-hold poll consumes the answer — so the SAME question keeps rendering
 *   for a beat after the POST resolves. A naive `submitting` boolean owned by
 *   the question panel re-enables the moment the POST settles, and the user
 *   clicks Approve a second time against the still-pending question.
 *
 *   Worse, surfaces that burst-poll with `authoritative: true` after a submit
 *   (the Outlook add-in's InstanceDetailDrawer) can briefly surface a frame
 *   that drops `hitl_question` and UNMOUNTS the question panel. Any latch held
 *   as the panel's local state is lost on that remount.
 *
 *   This hook holds the latch as a ref keyed on the question's content identity
 *   (`hitlQuestionKey`, SAS-normalized so it's stable across polls). Call it
 *   from a component that stays mounted across the whole HITL window (the
 *   drawer / live body), NOT from the per-question panel — the ref then
 *   survives the panel remounting. The latch releases automatically when a
 *   genuinely DIFFERENT, non-null question key appears (the next gate) or when
 *   the resume call throws (so the user can retry the SAME question). A
 *   transient null question (mid-gate poll/remount churn) does NOT release it —
 *   otherwise the pill re-enables for a beat and the AM can double-submit.
 *
 * Canonical latch for both surfaces (ui/CLAUDE.md add-in/ora parity rule):
 * ora's WorkflowDetailDrawer implements the same logic inline today and should
 * migrate onto this hook so the two surfaces can never drift again.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { DecisionAnswerOutcome } from "./decisionAnswerOutcome";
import { hitlQuestionKey } from "./hitlQuestionKey";

export interface UseHitlSubmitLatchArgs {
  /** The live HITL question (or null when none is pending). */
  hitlQuestion: unknown;
  /**
   * Performs the resume transport (e.g. `orchestratorApi.resumeInstance`).
   *
   * ⚠️ THE `| void` IS LOAD-BEARING, NOT STYLISTIC. The existing suites that AC-6
   * requires to pass UNMODIFIED type this prop as
   * `(payload) => Promise<void>` and pass `jest.fn().mockResolvedValue(undefined)`
   * at every call site. Narrowing this to `Promise<DecisionAnswerOutcome>` would
   * make those files a type error AND make a body that branches on `outcome.kind`
   * throw on `undefined` — landing in the catch below, clearing the ref, and
   * failing the double-submit tests. Those files may not be edited to fix it.
   *
   * `undefined` therefore means ACCEPTED, and must take the success branch.
   */
  onResume: (
    payload: Record<string, unknown>,
  ) => Promise<DecisionAnswerOutcome | void>;
  /** Optional post-submit hook (e.g. trigger a burst poll). Runs on success. */
  onResponded?: () => void;
  /** Fallback error message when the resume rejects without an Error message. */
  fallbackErrorMessage?: string;
}

export interface HitlSubmitLatch {
  /** True while the POST is in flight OR the latch is held for this question. */
  isSubmitting: boolean;
  /** Submit error for the current question; cleared when the question changes. */
  error: string | null;
  /**
   * Set when the substrate REFUSED this answer because the decision had already
   * been answered — on another surface, or by another person.
   *
   * DISTINCT FROM `error`, and the distinction is the point of this story: an
   * error means "try again" and releases the controls; a refusal means "the
   * substrate was right and your click did not land", and the controls stay held.
   * Re-enabling them here would reintroduce the double-submit this hook exists to
   * prevent, in the one case where the substrate has already told us it is wrong.
   */
  refusal: { reason: "already-answered"; detail: string | null } | null;
  /** Submit the answer for the current question (latches the controls). */
  submit: (payload: Record<string, unknown>) => Promise<void>;
}

const DEFAULT_FALLBACK = "Failed to submit. Please try again.";

export function useHitlSubmitLatch({
  hitlQuestion,
  onResume,
  onResponded,
  fallbackErrorMessage = DEFAULT_FALLBACK,
}: UseHitlSubmitLatchArgs): HitlSubmitLatch {
  // Content key of the question the user has answered. Survives panel remounts
  // because this hook is hosted by a stable parent (see file docstring).
  const submittedQuestionKeyRef = useRef<string | null>(null);
  const [postInFlight, setPostInFlight] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<
    { reason: "already-answered"; detail: string | null } | null
  >(null);

  const currentQuestionKey =
    hitlQuestion != null ? hitlQuestionKey(hitlQuestion) : null;

  // The question key the current `error`/`refusal` belongs to. Both are only
  // ever produced by `submit`, which knows the key it submitted for, so this is
  // the identity the clearing effect below compares against — NOT the live key,
  // which is exactly what made the old formula clear on a frame that was not a
  // new gate at all.
  const feedbackForKeyRef = useRef<string | null>(null);

  // Drop a stale error — and a stale refusal — when the substrate advances to a
  // new question. The refusal clears on exactly the same trigger as the error:
  // a genuinely different, non-null question key.
  //
  // BOTH guards are load-bearing, and both mirror the latch-release effect
  // below:
  //   * a null key is the documented transient burst-poll frame, never a new
  //     gate — clearing there wiped the refusal a beat after it was set, the
  //     answered card's null-guard fired, and the card vanished for good;
  //   * the SAME key returning after that null frame is still the same gate, so
  //     it must not clear either — the effect re-runs on null→A because the dep
  //     changed, and a bare `!== null` guard would drop the refusal one frame
  //     later instead of never.
  useEffect(() => {
    if (currentQuestionKey === null) return;
    if (currentQuestionKey === feedbackForKeyRef.current) return;
    feedbackForKeyRef.current = null;
    setError(null);
    setRefusal(null);
  }, [currentQuestionKey]);

  // Release the latch ONLY when a genuinely different, non-null question (the
  // next gate) appears — never on a transient frame where hitl_question briefly
  // drops to null. The warm-hold pump (and the post-submit authoritative
  // burst-poll) can surface a null-then-same-key frame for up to a full poll
  // interval after Approve; the old formula released on that null, re-enabling
  // the pill mid-gate and letting the AM click Approve twice. Clearing the ref
  // here (only on a real next gate) keeps the following gate's controls
  // interactive while the isSubmitting formula below holds the latch across the
  // transient null of the CURRENT gate.
  useEffect(() => {
    if (
      currentQuestionKey !== null &&
      submittedQuestionKeyRef.current !== null &&
      currentQuestionKey !== submittedQuestionKeyRef.current
    ) {
      submittedQuestionKeyRef.current = null;
    }
  }, [currentQuestionKey]);

  const isSubmitting =
    postInFlight ||
    (submittedQuestionKeyRef.current !== null &&
      (currentQuestionKey === null ||
        currentQuestionKey === submittedQuestionKeyRef.current));

  const submit = useCallback(
    async (payload: Record<string, unknown>) => {
      // Latch THIS question's controls disabled until the substrate moves on.
      submittedQuestionKeyRef.current = currentQuestionKey;
      // Any error/refusal this call produces belongs to THIS question — stamped
      // before the await so the clearing effect can tell "same gate" from "next
      // gate" without consulting the live key, which goes transiently null.
      feedbackForKeyRef.current = currentQuestionKey;
      setPostInFlight(true);
      setError(null);
      try {
        const outcome = await onResume(payload);
        if (outcome && outcome.kind === "refused") {
          // REFUSED. The latch stays HELD — the ref is deliberately not cleared —
          // which is what makes "the surface submits nothing further for that
          // decision" true rather than aspirational. `onResponded` does NOT fire:
          // nothing was responded to, and firing it would kick off a burst poll
          // for an advance that is not coming.
          setRefusal({ reason: outcome.reason, detail: outcome.detail });
          return;
        }
        if (outcome && outcome.kind === "error") {
          // Classified by the transport rather than thrown. Same handling as a
          // throw: release so the AM can retry the SAME question.
          submittedQuestionKeyRef.current = null;
          setError(outcome.message || fallbackErrorMessage);
          return;
        }
        // `accepted`, or `undefined` from a transport that returns nothing.
        onResponded?.();
        // Intentional: do NOT clear the latch on success. isSubmitting stays
        // true while the same question is on screen (and across any transient
        // null frame) and releases on its own only when a different, non-null
        // question key appears (the next gate) — see the effect above.
      } catch (err) {
        // Release the latch so the user can retry the SAME question.
        submittedQuestionKeyRef.current = null;
        setError(err instanceof Error ? err.message : fallbackErrorMessage);
      } finally {
        setPostInFlight(false);
      }
    },
    [currentQuestionKey, onResume, onResponded, fallbackErrorMessage],
  );

  return { isSubmitting, error, refusal, submit };
}

export default useHitlSubmitLatch;
